import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';

import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

// ─── Data ────────────────────────────────────────────────────────────────────

const FAQ_CATEGORIES = [
  { id: 'all', label: 'Tout', icon: 'LayoutGrid' },
  { id: 'account', label: 'Compte', icon: 'UserCircle' },
  { id: 'property', label: 'Propriétés', icon: 'Home' },
  { id: 'appointments', label: 'Rendez-vous', icon: 'Calendar' },
  { id: 'payments', label: 'Paiements', icon: 'CreditCard' },
  { id: 'legal', label: 'Légal', icon: 'Scale' },
];

const FAQS = [
  {
    id: 1,
    category: 'account',
    question: 'Comment créer un compte sur Kapital States ?',
    answer: "Cliquez sur « Connexion » en haut à droite, puis sélectionnez « Créer un compte ». Renseignez votre nom, adresse e-mail et un mot de passe sécurisé. Un e-mail de confirmation vous sera envoyé pour activer votre compte.",
  },
  {
    id: 2,
    category: 'account',
    question: 'Comment réinitialiser mon mot de passe ?',
    answer: "Sur la page de connexion, cliquez sur « Mot de passe oublié ? ». Entrez votre adresse e-mail et nous vous enverrons un lien de réinitialisation valable 24 heures.",
  },
  {
    id: 3,
    category: 'account',
    question: 'Comment modifier mes informations de profil ?',
    answer: "Accédez à « Mon Profil » depuis le menu de navigation. Vous pouvez y modifier votre nom, numéro de téléphone, préférences de recherche et paramètres de notification.",
  },
  {
    id: 4,
    category: 'property',
    question: 'Comment sauvegarder une propriété dans mes favoris ?',
    answer: "Sur la fiche d'une propriété, cliquez sur l'icône cœur en haut à droite de la photo. La propriété sera ajoutée à vos favoris, accessibles depuis votre profil.",
  },
  {
    id: 5,
    category: 'property',
    question: 'Comment créer une alerte pour de nouvelles propriétés ?',
    answer: "Dans votre profil, rendez-vous dans l'onglet « Alertes ». Définissez vos critères (commune, type, budget) et activez l'alerte. Vous recevrez un e-mail dès qu'une propriété correspondante est publiée.",
  },
  {
    id: 6,
    category: 'property',
    question: 'Comment contacter un agent pour une propriété ?',
    answer: "Sur la page de la propriété, utilisez le formulaire « Demande d'information » ou cliquez sur « Planifier une visite ». Un agent vous contactera dans les 24 heures ouvrables.",
  },
  {
    id: 7,
    category: 'appointments',
    question: 'Comment planifier une visite de propriété ?',
    answer: "Sur la fiche propriété, cliquez sur « Planifier une visite ». Choisissez une date et un créneau disponibles dans le calendrier. Vous recevrez une confirmation par e-mail et SMS.",
  },
  {
    id: 8,
    category: 'appointments',
    question: 'Comment annuler ou reporter un rendez-vous ?',
    answer: "Accédez à « Mon Profil » > onglet « Rendez-vous ». Sélectionnez le rendez-vous concerné et choisissez « Annuler » ou « Reporter ». Le report est possible jusqu'à 24h avant le rendez-vous.",
  },
  {
    id: 9,
    category: 'appointments',
    question: 'Puis-je prendre rendez-vous avec un notaire en ligne ?',
    answer: "Oui, via la section « Notaires » du menu. Vous pouvez rechercher un notaire par commune, consulter ses disponibilités et réserver directement en ligne.",
  },
  {
    id: 10,
    category: 'payments',
    question: 'Quels modes de paiement sont acceptés ?',
    answer: "Nous acceptons les virements bancaires, les cartes de crédit/débit (Visa, Mastercard) et les paiements mobiles locaux. Tous les paiements sont sécurisés et chiffrés.",
  },
  {
    id: 11,
    category: 'payments',
    question: 'Comment obtenir une facture pour mes transactions ?',
    answer: "Les factures sont automatiquement générées et envoyées par e-mail après chaque transaction. Vous pouvez aussi les télécharger depuis votre tableau de bord dans « Mon Compte ».",
  },
  {
    id: 12,
    category: 'legal',
    question: 'Quels documents sont nécessaires pour acheter une propriété ?',
    answer: "En Côte d'Ivoire, vous aurez besoin d'une pièce d'identité valide, d'un justificatif de domicile, d'un relevé bancaire récent et, selon le cas, d'un certificat de mariage. Notre équipe légale vous guidera étape par étape.",
  },
  {
    id: 13,
    category: 'legal',
    question: 'Qu\'est-ce qu\'un titre foncier et comment le vérifier ?',
    answer: "Le titre foncier est le document officiel attestant la propriété d'un bien immobilier. Kapital States vérifie systématiquement l'authenticité des titres fonciers avant toute mise en vente. Vous pouvez aussi utiliser notre service de vérification légale.",
  },
];

const VIDEO_TUTORIALS = [
  {
    id: 1,
    title: 'Démarrer avec Kapital States',
    description: 'Créez votre compte, configurez votre profil et lancez votre première recherche.',
    duration: '3:42',
    category: 'Démarrage',
    thumbnail: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=640&q=80',
    alt: 'Immeuble moderne à Abidjan avec façade vitrée reflétant le ciel bleu',
    videoUrl: '#',
  },
  {
    id: 2,
    title: 'Rechercher et filtrer des propriétés',
    description: 'Maîtrisez les filtres avancés pour trouver exactement ce que vous cherchez.',
    duration: '5:15',
    category: 'Recherche',
    thumbnail: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=640&q=80',
    alt: 'Vue aérienne de propriétés résidentielles dans un quartier verdoyant',
    videoUrl: '#',
  },
  {
    id: 3,
    title: 'Planifier une visite en ligne',
    description: 'Réservez une visite physique ou virtuelle en quelques clics.',
    duration: '2:58',
    category: 'Rendez-vous',
    thumbnail: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=640&q=80',
    alt: 'Salon lumineux d\'une villa moderne avec canapé blanc et grandes fenêtres',
    videoUrl: '#',
  },
  {
    id: 4,
    title: 'Configurer vos alertes immobilières',
    description: 'Recevez des notifications instantanées pour les nouvelles propriétés.',
    duration: '4:20',
    category: 'Alertes',
    thumbnail: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=640&q=80',
    alt: 'Villa contemporaine avec piscine extérieure et jardin tropical',
    videoUrl: '#',
  },
  {
    id: 5,
    title: 'Comprendre le processus d\'achat',
    description: 'De la recherche à la signature chez le notaire, toutes les étapes expliquées.',
    duration: '8:30',
    category: 'Légal',
    thumbnail: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=640&q=80',
    alt: 'Bureau de notaire avec documents légaux et stylo sur table en bois',
    videoUrl: '#',
  },
  {
    id: 6,
    title: 'Utiliser le calculateur ROI',
    description: 'Analysez la rentabilité d\'un investissement immobilier avec notre outil.',
    duration: '6:10',
    category: 'Investissement',
    thumbnail: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=640&q=80',
    alt: 'Graphiques financiers sur écran d\'ordinateur montrant des tendances immobilières',
    videoUrl: '#',
  },
];

const TROUBLESHOOTING = [
  {
    id: 1,
    icon: 'AlertCircle',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    title: 'Impossible de se connecter',
    steps: [
      'Vérifiez que votre adresse e-mail est correctement saisie.',
      'Assurez-vous que le verrouillage des majuscules est désactivé.',
      'Essayez de réinitialiser votre mot de passe via « Mot de passe oublié ».',
      'Videz le cache de votre navigateur et réessayez.',
      'Si le problème persiste, contactez notre support.',
    ],
  },
  {
    id: 2,
    icon: 'WifiOff',
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    title: 'Les photos de propriétés ne se chargent pas',
    steps: [
      'Vérifiez votre connexion internet.',
      'Actualisez la page (Ctrl+R ou Cmd+R).',
      'Désactivez temporairement votre bloqueur de publicités.',
      'Essayez un autre navigateur (Chrome, Firefox, Safari).',
      'Vérifiez que JavaScript est activé dans votre navigateur.',
    ],
  },
  {
    id: 3,
    icon: 'Calendar',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    title: 'Je ne reçois pas les e-mails de confirmation',
    steps: [
      'Vérifiez votre dossier spam/courrier indésirable.',
      'Ajoutez noreply@kapitalstates.com à vos contacts.',
      'Vérifiez l\'adresse e-mail dans votre profil.',
      'Attendez 5 minutes, les e-mails peuvent être retardés.',
      'Contactez le support si vous n\'avez rien reçu après 30 minutes.',
    ],
  },
  {
    id: 4,
    icon: 'Bell',
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    title: 'Mes alertes ne fonctionnent pas',
    steps: [
      'Vérifiez que vos alertes sont bien activées dans votre profil.',
      'Contrôlez vos préférences de notification dans les paramètres.',
      'Assurez-vous que votre e-mail est vérifié.',
      'Vérifiez que vos critères d\'alerte ne sont pas trop restrictifs.',
      'Désactivez puis réactivez l\'alerte pour la réinitialiser.',
    ],
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const SearchBar = ({ value, onChange }) => (
  <div className="relative max-w-2xl mx-auto">
    <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
      <Icon name="Search" size={20} className="text-text-secondary" />
    </div>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e?.target?.value)}
      placeholder="Rechercher dans la base de connaissances…"
      className="w-full pl-12 pr-4 py-4 bg-white border border-border rounded-2xl text-sm text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary shadow-ios-light transition-all"
    />
    {value && (
      <button
        onClick={() => onChange('')}
        className="absolute inset-y-0 right-4 flex items-center text-text-secondary hover:text-text-primary transition-colors"
      >
        <Icon name="X" size={16} />
      </button>
    )}
  </div>
);

const FAQItem = ({ faq }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`border rounded-2xl overflow-hidden transition-all duration-200 ${open ? 'border-primary/30 shadow-ios-light' : 'border-border'}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-white hover:bg-surface/50 transition-colors"
      >
        <span className="text-sm font-semibold text-text-primary pr-4">{faq?.question}</span>
        <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${open ? 'bg-primary text-white rotate-180' : 'bg-surface text-text-secondary'}`}>
          <Icon name="ChevronDown" size={14} />
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 bg-white border-t border-border/50">
          <p className="text-sm text-text-secondary leading-relaxed">{faq?.answer}</p>
        </div>
      )}
    </div>
  );
};

const VideoCard = ({ video }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="group bg-white rounded-2xl border border-border overflow-hidden shadow-ios-light hover:shadow-ios-medium transition-all duration-300 hover:-translate-y-0.5"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative aspect-video overflow-hidden bg-surface">
        <img
          src={video?.thumbnail}
          alt={video?.alt}
          className={`w-full h-full object-cover transition-transform duration-500 ${hovered ? 'scale-105' : 'scale-100'}`}
        />
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
            <Icon name="Play" size={22} className="text-primary ml-1" />
          </div>
        </div>
        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs font-medium px-2 py-0.5 rounded-lg">
          {video?.duration}
        </div>
        <div className="absolute top-2 left-2 bg-primary/90 text-white text-xs font-medium px-2 py-0.5 rounded-lg">
          {video?.category}
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-text-primary mb-1 group-hover:text-primary transition-colors">{video?.title}</h3>
        <p className="text-xs text-text-secondary leading-relaxed">{video?.description}</p>
      </div>
    </div>
  );
};

const TroubleshootingCard = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-2xl overflow-hidden transition-all duration-200 ${open ? `${item?.border} shadow-ios-light` : 'border-border'}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left bg-white hover:bg-surface/40 transition-colors"
      >
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${item?.bg}`}>
          <Icon name={item?.icon} size={18} className={item?.color} />
        </div>
        <span className="flex-1 text-sm font-semibold text-text-primary">{item?.title}</span>
        <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${open ? 'bg-surface rotate-180' : 'bg-surface'}`}>
          <Icon name="ChevronDown" size={14} className="text-text-secondary" />
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 bg-white border-t border-border/50">
          <ol className="space-y-2">
            {item?.steps?.map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-surface text-text-secondary text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                <span className="text-sm text-text-secondary leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

const ContactForm = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', category: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e?.target?.name]: e?.target?.value });

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mb-4">
          <Icon name="CheckCircle2" size={32} className="text-success" />
        </div>
        <h3 className="text-lg font-bold text-text-primary mb-2">Message envoyé !</h3>
        <p className="text-sm text-text-secondary max-w-sm">Notre équipe support vous répondra dans les 24 heures ouvrables. Vérifiez votre boîte e-mail.</p>
        <button
          onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', category: '', message: '' }); }}
          className="mt-6 px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-colors"
        >
          Envoyer un autre message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wide">Nom complet *</label>
          <input
            name="name"
            value={form?.name}
            onChange={handleChange}
            required
            placeholder="Jean Kouassi"
            className="w-full px-4 py-3 bg-surface border border-border rounded-xl text-sm text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wide">Adresse e-mail *</label>
          <input
            name="email"
            type="email"
            value={form?.email}
            onChange={handleChange}
            required
            placeholder="jean@exemple.com"
            className="w-full px-4 py-3 bg-surface border border-border rounded-xl text-sm text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wide">Catégorie</label>
        <select
          name="category"
          value={form?.category}
          onChange={handleChange}
          className="w-full px-4 py-3 bg-surface border border-border rounded-xl text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all appearance-none"
        >
          <option value="">Sélectionner une catégorie</option>
          <option value="account">Compte & Connexion</option>
          <option value="property">Propriétés</option>
          <option value="appointments">Rendez-vous</option>
          <option value="payments">Paiements</option>
          <option value="legal">Questions légales</option>
          <option value="technical">Problème technique</option>
          <option value="other">Autre</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wide">Sujet *</label>
        <input
          name="subject"
          value={form?.subject}
          onChange={handleChange}
          required
          placeholder="Décrivez brièvement votre problème"
          className="w-full px-4 py-3 bg-surface border border-border rounded-xl text-sm text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wide">Message *</label>
        <textarea
          name="message"
          value={form?.message}
          onChange={handleChange}
          required
          rows={5}
          placeholder="Décrivez votre problème en détail. Plus vous donnez d'informations, plus nous pourrons vous aider rapidement."
          className="w-full px-4 py-3 bg-surface border border-border rounded-xl text-sm text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full py-3.5 bg-primary text-white text-sm font-bold rounded-xl hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Envoi en cours…
          </>
        ) : (
          <>
            <Icon name="Send" size={16} />
            Envoyer le message
          </>
        )}
      </button>
    </form>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const KnowledgeBase = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeSection, setActiveSection] = useState('faq');

  const filteredFAQs = useMemo(() => {
    let items = FAQS;
    if (activeCategory !== 'all') items = items?.filter((f) => f?.category === activeCategory);
    if (searchQuery?.trim()) {
      const q = searchQuery?.toLowerCase();
      items = items?.filter((f) => f?.question?.toLowerCase()?.includes(q) || f?.answer?.toLowerCase()?.includes(q));
    }
    return items;
  }, [searchQuery, activeCategory]);

  const filteredVideos = useMemo(() => {
    if (!searchQuery?.trim()) return VIDEO_TUTORIALS;
    const q = searchQuery?.toLowerCase();
    return VIDEO_TUTORIALS?.filter((v) => v?.title?.toLowerCase()?.includes(q) || v?.description?.toLowerCase()?.includes(q) || v?.category?.toLowerCase()?.includes(q));
  }, [searchQuery]);

  const filteredTroubleshooting = useMemo(() => {
    if (!searchQuery?.trim()) return TROUBLESHOOTING;
    const q = searchQuery?.toLowerCase();
    return TROUBLESHOOTING?.filter((t) => t?.title?.toLowerCase()?.includes(q) || t?.steps?.some((s) => s?.toLowerCase()?.includes(q)));
  }, [searchQuery]);

  const sections = [
    { id: 'faq', label: 'FAQ', icon: 'HelpCircle', count: filteredFAQs?.length },
    { id: 'videos', label: 'Tutoriels vidéo', icon: 'PlayCircle', count: filteredVideos?.length },
    { id: 'troubleshooting', label: 'Dépannage', icon: 'Wrench', count: filteredTroubleshooting?.length },
    { id: 'contact', label: 'Contacter le support', icon: 'MessageSquare', count: null },
  ];

  return (
    <>
      <Helmet>
        <title>Centre d'aide – Kapital States</title>
        <meta name="description" content="Base de connaissances Kapital States : FAQs, tutoriels vidéo, guides de dépannage et formulaire de contact support." />
      </Helmet>
      <div className="min-h-screen bg-surface">
        <Header />

        {/* Hero */}
        <div className="bg-white border-b border-border/50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 text-center">
            <div className="inline-flex items-center gap-2 bg-primary/8 text-primary text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <Icon name="LifeBuoy" size={13} />
              Centre d'aide
            </div>
            <h1 className="text-3xl sm:text-4xl font-display font-display-semibold text-foreground mb-3 tracking-tight">
              Comment pouvons-nous vous aider ?
            </h1>
            <p className="text-base text-text-secondary mb-8 max-w-xl mx-auto">
              Trouvez des réponses instantanées, regardez nos tutoriels ou contactez notre équipe support.
            </p>
            <SearchBar value={searchQuery} onChange={setSearchQuery} />

            {/* Quick stats */}
            <div className="flex items-center justify-center gap-6 mt-8 flex-wrap">
              {[
                { icon: 'FileQuestion', label: `${FAQS?.length} articles FAQ` },
                { icon: 'Video', label: `${VIDEO_TUTORIALS?.length} tutoriels vidéo` },
                { icon: 'Clock', label: 'Réponse < 24h' },
              ]?.map((stat) => (
                <div key={stat?.label} className="flex items-center gap-1.5 text-xs text-text-secondary">
                  <Icon name={stat?.icon} size={14} className="text-primary" />
                  <span>{stat?.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section tabs */}
        <div className="bg-white border-b border-border/50 sticky top-[65px] z-40">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="flex gap-1 overflow-x-auto scrollbar-hide py-1">
              {sections?.map((s) => (
                <button
                  key={s?.id}
                  onClick={() => setActiveSection(s?.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${
                    activeSection === s?.id
                      ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <Icon name={s?.icon} size={15} />
                  {s?.label}
                  {s?.count !== null && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${activeSection === s?.id ? 'bg-primary/10 text-primary' : 'bg-surface text-text-secondary'}`}>
                      {s?.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">

          {/* ── FAQ ── */}
          {activeSection === 'faq' && (
            <div>
              {/* Category filter */}
              <div className="flex gap-2 flex-wrap mb-6">
                {FAQ_CATEGORIES?.map((cat) => (
                  <button
                    key={cat?.id}
                    onClick={() => setActiveCategory(cat?.id)}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all ${
                      activeCategory === cat?.id
                        ? 'bg-primary text-white shadow-ios-light'
                        : 'bg-white text-text-secondary border border-border hover:border-primary/40 hover:text-primary'
                    }`}
                  >
                    <Icon name={cat?.icon} size={13} />
                    {cat?.label}
                  </button>
                ))}
              </div>

              {filteredFAQs?.length > 0 ? (
                <div className="space-y-3">
                  {filteredFAQs?.map((faq) => (
                    <FAQItem key={faq?.id} faq={faq} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Icon name="SearchX" size={40} className="text-text-secondary mx-auto mb-3" />
                  <p className="text-sm font-semibold text-text-primary mb-1">Aucun résultat trouvé</p>
                  <p className="text-xs text-text-secondary">Essayez d'autres mots-clés ou <button onClick={() => setActiveSection('contact')} className="text-primary underline">contactez notre support</button>.</p>
                </div>
              )}
            </div>
          )}

          {/* ── Video Tutorials ── */}
          {activeSection === 'videos' && (
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-text-primary mb-1">Tutoriels vidéo</h2>
                <p className="text-sm text-text-secondary">Apprenez à utiliser Kapital States avec nos guides visuels pas-à-pas.</p>
              </div>
              {filteredVideos?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredVideos?.map((video) => (
                    <VideoCard key={video?.id} video={video} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Icon name="VideoOff" size={40} className="text-text-secondary mx-auto mb-3" />
                  <p className="text-sm font-semibold text-text-primary">Aucun tutoriel trouvé</p>
                  <p className="text-xs text-text-secondary mt-1">Essayez d'autres mots-clés.</p>
                </div>
              )}
            </div>
          )}

          {/* ── Troubleshooting ── */}
          {activeSection === 'troubleshooting' && (
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-text-primary mb-1">Guides de dépannage</h2>
                <p className="text-sm text-text-secondary">Résolvez les problèmes courants étape par étape, sans attendre.</p>
              </div>
              {filteredTroubleshooting?.length > 0 ? (
                <div className="space-y-3">
                  {filteredTroubleshooting?.map((item) => (
                    <TroubleshootingCard key={item?.id} item={item} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Icon name="SearchX" size={40} className="text-text-secondary mx-auto mb-3" />
                  <p className="text-sm font-semibold text-text-primary">Aucun guide trouvé</p>
                  <p className="text-xs text-text-secondary mt-1">Essayez d'autres mots-clés ou contactez le support.</p>
                </div>
              )}

              {/* Still need help? */}
              <div className="mt-8 bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/20 rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Icon name="HeadphonesIcon" size={24} className="text-primary" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <p className="text-sm font-bold text-text-primary">Vous n'avez pas trouvé la solution ?</p>
                  <p className="text-xs text-text-secondary mt-0.5">Notre équipe support est disponible du lundi au vendredi, 8h–18h.</p>
                </div>
                <button
                  onClick={() => setActiveSection('contact')}
                  className="px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-colors flex-shrink-0"
                >
                  Contacter le support
                </button>
              </div>
            </div>
          )}

          {/* ── Contact Support ── */}
          {activeSection === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Form */}
              <div className="lg:col-span-2 bg-white rounded-2xl border border-border p-6 shadow-ios-light">
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-text-primary mb-1">Contacter le support</h2>
                  <p className="text-sm text-text-secondary">Décrivez votre problème et nous vous répondrons dans les 24 heures ouvrables.</p>
                </div>
                <ContactForm />
              </div>

              {/* Sidebar info */}
              <div className="space-y-4">
                {/* Contact channels */}
                <div className="bg-white rounded-2xl border border-border p-5 shadow-ios-light">
                  <h3 className="text-sm font-bold text-text-primary mb-4">Autres canaux</h3>
                  <div className="space-y-3">
                    {[
                      { icon: 'Mail', label: 'E-mail', value: 'support@kapitalstates.com', color: 'text-blue-600', bg: 'bg-blue-50' },
                      { icon: 'Phone', label: 'Téléphone', value: '+225 27 22 XX XX XX', color: 'text-green-600', bg: 'bg-green-50' },
                      { icon: 'MessageCircle', label: 'WhatsApp', value: '+225 07 XX XX XX XX', color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    ]?.map((ch) => (
                      <div key={ch?.label} className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${ch?.bg}`}>
                          <Icon name={ch?.icon} size={15} className={ch?.color} />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-text-secondary">{ch?.label}</p>
                          <p className="text-xs text-text-primary">{ch?.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Hours */}
                <div className="bg-white rounded-2xl border border-border p-5 shadow-ios-light">
                  <h3 className="text-sm font-bold text-text-primary mb-3">Horaires du support</h3>
                  <div className="space-y-2">
                    {[
                      { day: 'Lundi – Vendredi', hours: '8h00 – 18h00' },
                      { day: 'Samedi', hours: '9h00 – 13h00' },
                      { day: 'Dimanche', hours: 'Fermé' },
                    ]?.map((h) => (
                      <div key={h?.day} className="flex justify-between items-center text-xs">
                        <span className="text-text-secondary">{h?.day}</span>
                        <span className={`font-semibold ${h?.hours === 'Fermé' ? 'text-error' : 'text-text-primary'}`}>{h?.hours}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Response time */}
                <div className="bg-success/8 border border-success/20 rounded-2xl p-4 flex items-start gap-3">
                  <Icon name="Clock" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-text-primary">Temps de réponse moyen</p>
                    <p className="text-xs text-text-secondary mt-0.5">Moins de 4 heures en jours ouvrables.</p>
                  </div>
                </div>

                {/* Quick links */}
                <div className="bg-white rounded-2xl border border-border p-5 shadow-ios-light">
                  <h3 className="text-sm font-bold text-text-primary mb-3">Liens rapides</h3>
                  <div className="space-y-2">
                    {[
                      { label: 'Consulter les FAQs', action: () => setActiveSection('faq'), icon: 'HelpCircle' },
                      { label: 'Voir les tutoriels', action: () => setActiveSection('videos'), icon: 'PlayCircle' },
                      { label: 'Guides de dépannage', action: () => setActiveSection('troubleshooting'), icon: 'Wrench' },
                    ]?.map((link) => (
                      <button
                        key={link?.label}
                        onClick={link?.action}
                        className="w-full flex items-center gap-2 text-xs text-primary hover:text-primary/80 font-medium transition-colors text-left"
                      >
                        <Icon name={link?.icon} size={13} />
                        {link?.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default KnowledgeBase;
